'use client'

import { useQuery } from '@tanstack/react-query'

import {
  fetchPortfolioLineChart,
  fetchPortfolioLineChartList,
} from '@/service/portfolio-line-chart.api'
import { TradeVenue } from '@/types/generated/common/types'
import type { PortfolioLineChart } from '@/types/generated/public_api'

export type PortfolioLineChartParams = {
  accountId: string
  tradeVenue: TradeVenue
  /** candle/bar interval in seconds (e.g. 300 or 3600) */
  timeframe: number
  /** inclusive history start, Unix timestamp in seconds */
  from?: string
}

export type PortfolioLineChartListParams = {
  /** ALL Accounts mode's scoped account ids — must be non-empty (an empty
   * array on the wire means "every account the caller can read", which is
   * NOT what an explicit empty selection should request). */
  accountIds: string[]
  tradeVenue: TradeVenue
  timeframe: number
  from?: string
}

export const portfolioLineChartKeys = {
  all: ['portfolioLineChart'] as const,
  chart: (accountId: string, tradeVenue: number, timeframe: number, from?: string) =>
    [...portfolioLineChartKeys.all, accountId, tradeVenue, timeframe, from] as const,
  // Sorted so account-list reordering (same underlying scope) doesn't cause a
  // spurious refetch/cache-miss.
  list: (accountIds: string[], tradeVenue: number, timeframe: number, from?: string) =>
    [
      ...portfolioLineChartKeys.all,
      'list',
      [...accountIds].sort().join(','),
      tradeVenue,
      timeframe,
      from,
    ] as const,
}

export function usePortfolioLineChart(
  params: PortfolioLineChartParams | null,
  refetchInterval: number | false = false
) {
  return useQuery({
    // Hourly resolutions (1W/1M/ALL) share the same one-year response and are
    // windowed client-side. Include `from` so a changed history boundary cannot
    // reuse an older query response.
    queryKey: params
      ? portfolioLineChartKeys.chart(
          params.accountId,
          params.tradeVenue,
          params.timeframe,
          params.from
        )
      : portfolioLineChartKeys.all,
    queryFn: () =>
      fetchPortfolioLineChart({
        accountId: params!.accountId,
        accountIds: [],
        tradeVenue: params!.tradeVenue,
        timeframe: params!.timeframe,
        from: params!.from,
      }),
    enabled: !!params?.accountId,
    refetchInterval,
    staleTime: 60_000, // 1 minute
    // Keep showing the current series while a timeframe switch fetches, but
    // never carry one account's chart over to another account.
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[1] === params?.accountId ? prev : undefined,
    // History only needs to be pulled once per account/timeframe — live
    // movement between refetches comes from the WS-pushed SUMMARY/PORTFOLIO
    // streams instead (see account-cards.tsx). Left at the library default,
    // `refetchOnWindowFocus` re-issued this EXACT request (same queryKey, no
    // state/prop change at all) every time the browser tab regained focus —
    // switching tabs or opening devtools was enough — so it's disabled here;
    // a failed fetch is instead retried on the next account/timeframe change.
    refetchOnWindowFocus: false,
    retry: false,
  })
}

/**
 * Query-options factory for the list (multi-account) chart fetch — shared by
 * `usePortfolioLineChartList` below AND by callers that need to fan this out
 * per-venue via `useQueries` (e.g. account-cards.tsx's "All Accounts" mode:
 * `trade_venue` is still a required singular field on the request, and
 * whether the backend accepts a single request whose `accountIds` spans
 * MULTIPLE venues hasn't been verified live, so the safer default is one
 * request per distinct venue among the scoped accounts — this factory is
 * what makes that `useQueries` fan-out possible without duplicating the
 * queryKey/queryFn/staleTime/retry shape).
 */
export function portfolioLineChartListQueryOptions(params: PortfolioLineChartListParams) {
  return {
    queryKey: portfolioLineChartKeys.list(
      params.accountIds,
      params.tradeVenue,
      params.timeframe,
      params.from
    ),
    queryFn: () =>
      fetchPortfolioLineChartList({
        accountId: '',
        accountIds: params.accountIds,
        tradeVenue: params.tradeVenue,
        timeframe: params.timeframe,
        from: params.from,
      }),
    staleTime: 60_000,
    // See usePortfolioLineChart's matching option — avoids re-issuing the
    // same request on every browser tab focus.
    refetchOnWindowFocus: false,
    retry: false as const,
  }
}

/**
 * "All Accounts" mode's chart fetch — one request covering every scoped
 * account (`params.accountIds`), answered with `PortfolioLineChartList`
 * (see fetchPortfolioLineChartList's doc comment). Mirrors
 * `usePortfolioLineChart`'s shape/options so the two behave identically
 * apart from what's being requested; a caller mixing this with
 * `usePortfolioLineChart` (e.g. account-cards.tsx, switching between
 * single-account and ALL modes) must call BOTH hooks unconditionally on
 * every render (React's rules of hooks), passing `null` to whichever one is
 * inactive for the current mode.
 *
 * NOTE: if live verification (see portfolioLineChartListQueryOptions's doc
 * comment) finds that a single request can't span multiple trade venues,
 * prefer the `useQueries`-based per-venue fan-out over this hook directly.
 */
export function usePortfolioLineChartList(
  params: PortfolioLineChartListParams | null,
  refetchInterval: number | false = false
) {
  return useQuery({
    queryKey: params
      ? portfolioLineChartKeys.list(
          params.accountIds,
          params.tradeVenue,
          params.timeframe,
          params.from
        )
      : portfolioLineChartKeys.all,
    queryFn: () =>
      params ? portfolioLineChartListQueryOptions(params).queryFn() : Promise.resolve([]),
    staleTime: 60_000,
    retry: false,
    enabled: !!params && params.accountIds.length > 0,
    refetchInterval,
    // Compare the previous query's full account-scope key (queryKey[2], the
    // sorted joined accountIds string) — NOT array index/position — so a
    // scope change (not just a timeframe switch) never carries stale data
    // over from a different set of accounts.
    placeholderData: (prev: PortfolioLineChart[] | undefined, prevQuery) =>
      prevQuery?.queryKey[2] === [...(params?.accountIds ?? [])].sort().join(',')
        ? prev
        : undefined,
  })
}
